import { Camera, CheckCircle2, CloudOff, Database, List, MapPin, MapPinned, Plus, RefreshCw, Search, Settings, Sparkles, X } from 'lucide-react';
import type { AppCapabilities } from '../hooks/useAppCapabilities';
import type { LocationSection } from '../lib/groups';
import type { ItemRecord } from '../types';
import { CapabilityStrip } from './CapabilityStrip';
import { ItemPhoto } from './ItemPhoto';

type Props = {
  sections: LocationSection[];
  totalItems: number;
  nameReviewCount: number;
  locationReviewCount: number;
  outboxCount: number;
  query: string;
  syncMessage: string;
  syncing: boolean;
  syncConfigured: boolean;
  capabilities: AppCapabilities;
  grouped: boolean;
  onQueryChange: (query: string) => void;
  onOpenCamera: () => void;
  onOpenItem: (item: ItemRecord) => void;
  onOpenSettings: () => void;
  onReviewNames: () => void;
  onReviewLocations: () => void;
  onSync: () => void;
  onToggleGrouping: () => void;
};

export function GalleryScreen(props: Props) {
  const hasItems = props.sections.length > 0;
  return (
    <main className="gallery-screen">
      <header className="app-header">
        <button className="brand-button" type="button" onClick={props.onOpenCamera} aria-label="Open camera"><Database size={27} aria-hidden="true" /></button>
        <div className="title-block">
          <h1>Household items</h1>
          <p>{props.totalItems} item{props.totalItems === 1 ? '' : 's'} saved on this device</p>
        </div>
        <CapabilityStrip capabilities={props.capabilities} syncConfigured={props.syncConfigured} />
        <button className="icon-button" type="button" onClick={props.onOpenSettings} aria-label="Open app settings"><Settings size={22} /></button>
      </header>

      <section className="gallery-tools" aria-label="Find and capture items">
        <div className="search-field">
          <Search size={22} aria-hidden="true" />
          <input aria-label="Search household items" value={props.query} onChange={(event) => props.onQueryChange(event.target.value)} placeholder="Search key, chaabi, drawer, adhesive…" />
          {props.query ? <button type="button" aria-label="Clear search" onClick={() => props.onQueryChange('')}><X size={20} /></button> : null}
        </div>
        <button className={`button secondary group-button${props.grouped ? ' active' : ''}`} type="button" aria-label={props.grouped ? 'Show newest first' : 'Group by location'} aria-pressed={props.grouped} onClick={props.onToggleGrouping}>
          {props.grouped ? <List size={19} aria-hidden="true" /> : <MapPinned size={19} aria-hidden="true" />}
          <span>{props.grouped ? 'Newest first' : 'Group by place'}</span>
        </button>
        <button className="button primary add-button" type="button" onClick={props.onOpenCamera}><Plus size={21} /><span className="desktop-label">Add item</span><span className="mobile-label">Capture</span></button>
      </section>

      {props.nameReviewCount || props.locationReviewCount ? (
        <section className="review-sync-row">
          <div className="review-banner">
            <div className="review-summary">
              <Sparkles size={20} aria-hidden="true" />
              <span>{[props.nameReviewCount ? `${props.nameReviewCount} need name${props.nameReviewCount === 1 ? '' : 's'}` : '', props.locationReviewCount ? `${props.locationReviewCount} need${props.locationReviewCount === 1 ? 's' : ''} a place` : ''].filter(Boolean).join(' · ')}</span>
            </div>
            <div className="review-actions">
              {props.nameReviewCount ? <button type="button" onClick={props.onReviewNames}>Review names</button> : null}
              {props.locationReviewCount ? <button type="button" className="place" onClick={props.onReviewLocations}>Review places</button> : null}
            </div>
          </div>
          <SyncStrip {...props} />
        </section>
      ) : <SyncStrip {...props} />}

      <div className="gallery-content">
        {hasItems ? props.sections.map((section) => (
          <section className={`location-section${props.grouped ? '' : ' ungrouped'}`} key={section.title || 'recent'} aria-label={props.grouped ? section.title : 'Items, newest first'}>
            {props.grouped ? <header><h2>{section.title}</h2><span>{section.items.length}</span></header> : null}
            <div className="item-grid">
              {section.items.map((item) => <ItemTile key={item.id} item={item} onOpen={() => props.onOpenItem(item)} />)}
            </div>
          </section>
        )) : <EmptyGallery hasQuery={Boolean(props.query)} onCapture={props.onOpenCamera} onClear={() => props.onQueryChange('')} />}
      </div>
    </main>
  );
}

function SyncStrip(props: Props) {
  return (
    <div className="sync-strip" aria-live="polite" aria-busy={props.syncing}>
      {props.syncing ? <RefreshCw className="spin" size={19} aria-hidden="true" /> : props.syncConfigured ? <CheckCircle2 size={19} aria-hidden="true" /> : <CloudOff size={19} aria-hidden="true" />}
      <span>{props.syncMessage || (props.outboxCount ? `${props.outboxCount} change${props.outboxCount === 1 ? '' : 's'} waiting to sync` : 'Saved on this device')}</span>
      <button type="button" onClick={props.onSync} disabled={props.syncing}>{props.syncing ? 'Syncing…' : 'Sync now'}</button>
    </div>
  );
}

function ItemTile({ item, onOpen }: { item: ItemRecord; onOpen: () => void }) {
  const location = item.locationNote || item.locationGroup || 'Needs location';
  return (
    <button className="item-tile" type="button" onClick={onOpen}>
      <div className="item-photo-frame">
        <ItemPhoto photo={item.photo} photoMimeType={item.photoMimeType} alt="" className="item-photo" loading="lazy" />
        {!item.locationNote ? <span className="missing-place" aria-hidden="true"><MapPin size={13} />Place</span> : null}
      </div>
      <strong>{item.name}</strong>
      <span className={!item.locationNote ? 'missing-text' : ''}>{location}</span>
    </button>
  );
}

function EmptyGallery({ hasQuery, onCapture, onClear }: { hasQuery: boolean; onCapture: () => void; onClear: () => void }) {
  return (
    <section className="empty-gallery">
      <Database size={40} aria-hidden="true" />
      <h2>{hasQuery ? 'No match yet' : 'No items saved yet'}</h2>
      <p>{hasQuery ? 'Try another name, alias, Urdu word, or location.' : 'Capture the first item. It will stay on this device and work offline.'}</p>
      <div>
        {hasQuery ? <button className="button secondary" type="button" onClick={onClear}>Clear search</button> : null}
        <button className="button primary" type="button" onClick={onCapture}><Camera size={19} />Take a photo</button>
      </div>
    </section>
  );
}
